import { useEffect, useRef, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Toggle } from '@/components/ui/Toggle';
import { isValidEmail } from '@/utils/validation';
import { formatApiError } from '@/services/api';
import { magicLoginService } from '@/services/magic-login.service';
import { useAuth } from '@/hooks/useAuth';

interface MagicLoginPanelProps {
  initialEmail: string;
  /** Same-origin path to open after a successful login. */
  afterLoginPath?: string;
  onBackToPassword: () => void;
}

type Step = 'email' | 'sent';

/** Mirrors the server's per-recipient cooldown (a resend inside it is silently suppressed). */
const RESEND_COOLDOWN_SECONDS = 60;

/**
 * "Email me a login link" flow on the login page: email (+ remember me) -> check inbox + 6-digit code.
 * Rendered only when the server reports magicLogin enabled.
 */
export const MagicLoginPanel = ({ initialEmail, afterLoginPath = '/', onBackToPassword }: MagicLoginPanelProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { loginWithResponse } = useAuth();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(initialEmail);
  const [rememberMe, setRememberMe] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [resent, setResent] = useState(false);
  const isResendRef = useRef(false);
  const [resendWait, setResendWait] = useState(0);

  useEffect(() => {
    if (resendWait <= 0) return;
    const id = window.setTimeout(() => setResendWait((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendWait]);

  const errorText = (err: unknown): string => {
    const apiError = formatApiError(err);
    if (apiError.statusCode === 429) return t('auth_magic_error_rate_limited');
    if (apiError.statusCode === 401) return t('auth_magic_error_invalid_code');
    return t('auth_magic_error_generic');
  };

  const requestMutation = useMutation({
    mutationFn: () => magicLoginService.requestLink(email.trim()),
    onSuccess: () => {
      setCode('');
      setStep('sent');
      setResendWait(RESEND_COOLDOWN_SECONDS);
      setResent(isResendRef.current);
      isResendRef.current = false;
    },
    onError: (err: unknown) => {
      isResendRef.current = false;
      setError(errorText(err));
    },
  });

  const verifyMutation = useMutation({
    mutationFn: () => magicLoginService.verifyCode(email.trim(), code.trim(), rememberMe),
    onSuccess: async (response) => {
      await loginWithResponse(response.accessToken, response.user);
      navigate(afterLoginPath);
    },
    onError: (err: unknown) => setError(errorText(err)),
  });

  const handleRequest = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!isValidEmail(email)) {
      setError(t('auth_login_error_invalid_email'));
      return;
    }
    requestMutation.mutate();
  };

  const handleVerify = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code.trim())) {
      setError(t('auth_magic_error_invalid_code'));
      return;
    }
    verifyMutation.mutate();
  };

  const errorBox = error ? (
    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600" role="alert">
      {error}
    </div>
  ) : null;

  if (step === 'sent') {
    return (
      <div>
        <h2 className="text-base font-bold text-gray-900 text-center mb-2">{t('auth_magic_sent_heading')}</h2>
        <p className="text-sm text-gray-600 text-center mb-5">{t('auth_magic_sent_body', { email: email.trim() })}</p>
        {errorBox}
        <div aria-live="polite" className="text-sm text-green-700 text-center mb-3">
          {resent && !error && !requestMutation.isPending ? t('auth_magic_resent') : ''}
        </div>
        <form onSubmit={handleVerify} className="space-y-4">
          <Input
            inputSize="sm"
            labelVisible
            id="magic-code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder={t('auth_magic_code_placeholder')}
            disabled={verifyMutation.isPending}
            autoComplete="one-time-code"
            required
            label={t('auth_magic_code_label')}
          />
          <div className="flex items-center">
            <Toggle label={t('auth_login_remember_me')} checked={rememberMe} onChange={setRememberMe} />
          </div>
          <div className="text-center">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={verifyMutation.isPending}
              className="uppercase tracking-wide w-full sm:w-auto sm:px-12 !h-9 sm:!h-auto !text-xs sm:!text-base"
            >
              {t('auth_magic_code_button')}
            </Button>
          </div>
        </form>
        <div className="flex items-center justify-between mt-5 text-[11px] sm:text-[12px]">
          <button
            type="button"
            onClick={() => { setError(''); setResent(false); setStep('email'); }}
            className="text-gray-400 hover:text-secondary-500 transition-colors"
          >
            {t('auth_magic_use_other_email')}
          </button>
          <button
            type="button"
            onClick={() => { setError(''); setResent(false); isResendRef.current = true; requestMutation.mutate(); }}
            disabled={requestMutation.isPending || resendWait > 0}
            className="text-bs-secondary hover:text-secondary-600 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('auth_magic_resend')}{resendWait > 0 ? ` (${resendWait})` : ''}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-base font-bold text-gray-900 text-center mb-2">{t('auth_magic_heading')}</h2>
      <p className="text-sm text-gray-600 text-center mb-5">{t('auth_magic_intro')}</p>
      {errorBox}
      <form onSubmit={handleRequest} className="space-y-4">
        <Input
          inputSize="sm"
          labelVisible
          id="magic-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth_login_placeholder_email')}
          disabled={requestMutation.isPending}
          autoComplete="email"
          required
          label={t('auth_login_label_email')}
        />
        <div className="text-center">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={requestMutation.isPending}
            className="uppercase tracking-wide w-full sm:w-auto sm:px-12 !h-9 sm:!h-auto !text-xs sm:!text-base"
          >
            {t('auth_magic_send_button')}
          </Button>
        </div>
      </form>
      <div className="mt-5 text-center">
        <button
          type="button"
          onClick={onBackToPassword}
          className="text-[11px] sm:text-[12px] text-gray-400 hover:text-secondary-500 transition-colors"
        >
          {t('auth_magic_back_to_password')}
        </button>
      </div>
    </div>
  );
};
